import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CancelOrderDialogProps {
  /** The order being cancelled, or null when the dialog is closed. */
  orderId: string | null;
  onConfirm: (reason: string | undefined) => void;
  onDismiss: () => void;
}

/**
 * Cancelling is terminal — `VALID_NEXT_STATUSES.cancelled` is empty and the backend voids the
 * payment link on the way out — and on the board it is one drag away from "Completados". So the
 * board never fires it straight from a drop or a menu click: this destructive confirmation stands in
 * between, and it takes the reason the order detail records (`cancelledReason`) while it is there.
 */
export function CancelOrderDialog({ orderId, onConfirm, onDismiss }: CancelOrderDialogProps) {
  const { t } = useTranslation('orders');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (orderId) setReason('');
  }, [orderId]);

  return (
    <AlertDialog open={Boolean(orderId)} onOpenChange={(open) => !open && onDismiss()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('board.cancelDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('board.cancelDialog.description', { id: orderId?.slice(0, 8) ?? '' })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1.5">
          <label htmlFor="board-cancel-reason" className="text-sm text-muted-foreground">
            {t('board.cancelDialog.reasonLabel')}
          </label>
          <Textarea
            id="board-cancel-reason"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('board.cancelDialog.reasonPlaceholder')}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('board.cancelDialog.keep')}</AlertDialogCancel>
          <AlertDialogAction
            className={cn(buttonVariants({ variant: 'destructive' }))}
            onClick={() => onConfirm(reason.trim() || undefined)}
          >
            {t('board.cancelDialog.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
